import { z } from 'zod'

import type { AnswerUnit, Problem } from '~/@entities/Problem'
import type { SchoolType } from '~/@entities/SchoolSystem'

/***************** 채점 결과 *****************/
export const SCORING_RESULT = ['CORRECT', 'WRONG', 'UNKNOWN', 'NONE', 'RESTRICTED'] as const

export const ScoringResultSchema = z.enum(SCORING_RESULT)
export type ScoringResult = z.infer<typeof ScoringResultSchema>

// 비공개(RESTRICTED)는 시험이 끝나기 전이라 결과를 가린 문항이다(서버는 시험 채점에서만 낸다). 제출로 치지 않는다.
export const isSubmittedResult = (result: ScoringResult) =>
  result !== 'NONE' && result !== 'RESTRICTED'

export const isWrongOrUnknownResult = (result: ScoringResult) =>
  result === 'WRONG' || result === 'UNKNOWN'

/***************** 문항 채점 *****************/

// 채점 화면이 문항마다 읽는 문제 필드. 학습지·시험은 문제은행의 Problem 을, 교재는 교재 문제를 넘긴다.
export type ScorableProblem = Pick<
  Problem,
  | 'answer'
  | 'type'
  | 'keypadTypes'
  | 'answerData'
  | 'answerCount'
  | 'answerUnits'
  | 'level'
  | 'optionCount'
  | 'autoScoredType'
>

/**
 * 문항 하나의 채점. 서버의 채점 기록·문제와 화면이 아는 학습물 정보(문항 번호·자동채점 여부·학년)를 모은 값이다.
 * 어느 학습물의 문항인지(`Target`)는 학습지·시험·교재가 각자 정한다.
 */
export type ScoringItem<Target, P extends ScorableProblem> = Target & {
  result: ScoringResult
  // 빈 문자열 답은 없음으로 읽는다. 자동채점의 빈 입력과 선생님 채점이 같은 값을 써서 구분할 수 없다.
  userAnswer: string | null
  // 다중 셀 답. null 원소는 미입력 빈칸이다.
  userAnswers: readonly (string | null)[] | null
  updateDatetime: Date | null
  problem: P
  label: string
  // 학습물(학습지·시험·교재)이 자동채점인가
  autoScorable: boolean
  // 문항 자체가 자동채점 가능한가. 출제 뒤 바뀔 수 있어 학습물 설정과 따로 든다.
  problemAutoScorable: boolean
  schoolType: SchoolType | undefined
  // 학교급·학년(·학기) 표기. 학습지는 "중1", 교재는 "중 1(22)개정-1" 모양이다
  gradeLabel: string
  // 화면에 보여 줄 제출한 답. 다중 셀 답은 KaTeX 로 이어 붙인다
  submittedAnswer: string | null
  isSubmitted: boolean
  isWrongOrUnknown: boolean
  // 정답 AST 를 쓰는 키패드 문형은 answer 문자열에서 파생한 단위를 쓰지 않아 null 이다
  answerUnits: readonly AnswerUnit[] | null
  answerLength: number
}
